import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, ChevronDown, ChevronUp, ExternalLink, UserPlus, Link2, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Small strip under the WhatsApp chat header: "this person submitted a Facebook form". */
export const LeadFormBanner = ({ conversationId, linkedCustomerId, onLinked }) => {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    setMatches([]); setOpen(false);
    api.get(`/whatsapp/conversations/${conversationId}/lead-forms`)
      .then(({ data }) => { if (!cancelled) setMatches(data.matches || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [conversationId]);

  if (!matches.length) return null;
  const top = matches[0];
  const others = matches.slice(1);
  const answers = Object.entries(top.answers || {}).slice(0, 6);
  const alreadyLinkedToLead = linkedCustomerId && top.customer_id === linkedCustomerId;
  const showLink = !alreadyLinkedToLead && !linkedCustomerId;

  const linkLead = async () => {
    setLinking(true);
    try {
      const { data } = await api.post(`/whatsapp/conversations/${conversationId}/link-lead`, { lead_id: top.lead_id });
      toast.success(data.created
        ? `Customer ${data.customer_name} created from the form and linked to this chat`
        : `Chat linked to ${data.customer_name}`);
      setMatches((ms) => ms.map((m) => (m.lead_id === top.lead_id ? { ...m, converted: true, customer_id: data.customer_id } : m)));
      if (onLinked) onLinked(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not link the lead');
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="mx-3 mt-2 rounded-md border border-sky-200 bg-sky-50 dark:bg-sky-950/40 dark:border-sky-900 text-sky-900 dark:text-sky-100" data-testid="wa-lead-form-banner">
      <div className="flex items-center gap-2 px-3 py-1.5 text-xs">
        <ClipboardList className="w-3.5 h-3.5 flex-shrink-0 text-sky-600" />
        <span className="truncate">
          Submitted Facebook form <b data-testid="wa-lead-form-name">“{top.form_name}”</b>
          {top.submitted_at ? ` on ${fmt(top.submitted_at)}` : ''}
          {top.matched_by === 'name' && <span className="ml-1 text-sky-700/80">(matched by name — check the number)</span>}
          {others.length > 0 && <span className="ml-1 text-sky-700/80">+{others.length} more</span>}
        </span>
        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          {showLink && (
            <button type="button" onClick={linkLead} disabled={linking}
              className="inline-flex items-center gap-1 rounded bg-sky-600 hover:bg-sky-700 text-white px-2 py-0.5 font-medium disabled:opacity-60"
              data-testid="wa-lead-form-link-btn">
              {linking ? <Loader2 className="w-3 h-3 animate-spin" /> : (top.converted ? <Link2 className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />)}
              {top.converted ? 'Link customer' : 'Create & link customer'}
            </button>
          )}
          {alreadyLinkedToLead && <span className="text-sky-700/80" data-testid="wa-lead-form-linked">Linked to this lead</span>}
          <button type="button" onClick={() => navigate(`/meta-leads?highlight=${top.lead_id}`)}
            className="inline-flex items-center gap-1 text-sky-700 hover:underline" data-testid="wa-lead-form-open">
            <ExternalLink className="w-3 h-3" /> View lead
          </button>
          {(answers.length > 0 || others.length > 0 || top.ad_name) && (
            <button type="button" onClick={() => setOpen((o) => !o)} className="text-sky-700" data-testid="wa-lead-form-toggle" aria-label="Toggle form details">
              {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="px-3 pb-2 text-xs space-y-1 border-t border-sky-200/70 dark:border-sky-900 pt-1.5" data-testid="wa-lead-form-details">
          {top.ad_name && <p><span className="text-sky-700/80">Ad:</span> {top.ad_name}{top.campaign_name ? ` · ${top.campaign_name}` : ''}</p>}
          {answers.map(([k, v]) => (
            <p key={k}><span className="text-sky-700/80">{k.replace(/_/g, ' ')}:</span> {String(v)}</p>
          ))}
          {others.map((m) => (
            <p key={m.lead_id} className="text-sky-800/90">Also: “{m.form_name}”{m.submitted_at ? ` on ${fmt(m.submitted_at)}` : ''}{m.matched_by === 'name' ? ' (name match)' : ''}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadFormBanner;
